import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Bell } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/api/notifications';
import { usePageTitle } from '@/hooks/usePageTitle';
import { cn } from '@/lib/cn';
import { apiError } from '@/lib/api';

export function NotificationsPage() {
  usePageTitle('Notifications');
  const { data, isLoading, isError, error } = useNotifications();
  const markOne = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  // Auto-mark all unread as read after a short settling delay so the badge
  // clears once the user is actually viewing the list. Avoids the surprising
  // "I just read these but the count is still 12" behavior.
  const markedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const list = data ?? [];
    const unread = list.filter((n) => !n.is_read && !markedRef.current.has(n.id));
    if (unread.length === 0) return;
    const t = setTimeout(() => {
      for (const n of unread) {
        markedRef.current.add(n.id);
        markOne.mutate(n.id);
      }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <AppShell crumbs={[{ label: 'Notifications' }]}>
      <Card
        title="All notifications"
        actions={
          <Button
            variant="secondary"
            onClick={() => markAll.mutate()}
            loading={markAll.isPending}
            disabled={!(data ?? []).some((n) => !n.is_read)}
            className="text-xs"
          >
            Mark all read
          </Button>
        }
      >
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="text-sm text-red-600">{apiError(error)}</div>
        ) : (data?.length ?? 0) === 0 ? (
          <EmptyState
            title="No notifications"
            description="You're all caught up. New notifications will appear here when files are uploaded or requests are submitted."
            icon={<Bell size={24} />}
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {(data ?? []).map((n) => {
              const inner = (
                <>
                  <div className={cn('flex-1 min-w-0', n.link && 'group-hover:text-bb-navy transition-colors')}>
                    <div className="text-sm font-medium text-slate-800">{n.title}</div>
                    {n.body && <div className="text-sm text-slate-600 mt-0.5">{n.body}</div>}
                    <div className="text-xs text-slate-400 mt-1">
                      {format(new Date(n.created_at), 'PP p')}
                    </div>
                  </div>
                  {n.link && (
                    <span className="text-xs text-bb-navy shrink-0">Open →</span>
                  )}
                  {!n.is_read && !n.link && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); markOne.mutate(n.id); }}
                      className="text-xs text-slate-500 hover:text-bb-navy shrink-0"
                    >
                      Mark read
                    </button>
                  )}
                </>
              );
              const rowClass = cn(
                'group py-3 flex items-start gap-3 rounded',
                !n.is_read && 'bg-bb-mint/5 -mx-5 px-5',
                n.link && 'cursor-pointer hover:bg-slate-50 -mx-5 px-5',
              );
              return (
                <li key={n.id}>
                  {n.link ? (
                    <Link
                      to={n.link}
                      onClick={() => { if (!n.is_read) markOne.mutate(n.id); }}
                      className={rowClass}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div className={rowClass}>{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </AppShell>
  );
}
